<template>
  <div class="ecx-progress-bar w-full">
    <div v-if="showInfo" class="text-center mb-4">
      <div v-if="showCount" class="text-2xl font-semibold text-default-black mb-2">
        {{ current }}/{{ total }}
      </div>
      <div v-if="showPercentage" class="text-lg text-subtitle-gray">
        {{ percentage }}%
      </div>
    </div>

    <div
      class="relative overflow-hidden"
      :class="[sizeClasses, roundedClasses, barClass]"
      :style="{ backgroundColor: trackColor }"
    >
      <div
        class="h-full transition-all duration-300 ease-out"
        :class="[variantClasses, roundedClasses]"
        :style="{ width: `${percentage}%`, ...gradientStyle }"
      />
    </div>

    <div v-if="label" class="text-sm text-subtitle-gray mt-2 text-center">
      {{ label }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type ProgressVariant = 'primary' | 'success' | 'warning' | 'error' | 'gradient' | 'royal-gradient' | 'royal-fade-gradient'
export type ProgressSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ProgressBarProps {
  /** Valor atual */
  current: number
  /** Valor total */
  total: number
  /** Mostrar informações (count e/ou percentage) */
  showInfo?: boolean
  /** Mostrar contagem (X/Y) */
  showCount?: boolean
  /** Mostrar percentual */
  showPercentage?: boolean
  /** Variante de cor */
  variant?: ProgressVariant
  /** Tamanho da barra */
  size?: ProgressSize
  /** Cor da track (fundo) */
  trackColor?: string
  /** Classes adicionais para a barra */
  barClass?: string
  /** Label abaixo da barra */
  label?: string
  /** Cores do gradiente (para variant='gradient') */
  gradientFrom?: string
  /** Cor final do gradiente */
  gradientTo?: string
  /** Cantos arredondados (pill = full, square = sm) */
  rounded?: 'full' | 'sm' | 'none'
}

const props = withDefaults(defineProps<ProgressBarProps>(), {
  showInfo: false,
  showCount: true,
  showPercentage: true,
  variant: 'primary',
  size: 'md',
  trackColor: '#ECEEF3',
  gradientFrom: '#EBF0FF',
  gradientTo: '#3342FF',
  rounded: 'full',
})

const percentage = computed(() => {
  if (props.total === 0) return 0
  return Math.round((props.current / props.total) * 100)
})

const sizeClasses = computed(() => {
  const sizes: Record<ProgressSize, string> = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-2.5',
    xl: 'h-3',
  }
  return sizes[props.size]
})

const roundedClasses = computed(() => {
  const rounded: Record<string, string> = {
    full: 'rounded-full',
    sm: 'rounded-sm',
    none: 'rounded-none',
  }
  return rounded[props.rounded]
})

const variantClasses = computed(() => {
  if (props.variant === 'gradient' || props.variant === 'royal-gradient' || props.variant === 'royal-fade-gradient') {
    return ''
  }
  const variants: Record<ProgressVariant, string> = {
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
    gradient: '',
    'royal-gradient': '',
    'royal-fade-gradient': '',
  }
  return variants[props.variant]
})

const gradientStyle = computed(() => {
  if (props.variant === 'gradient') {
    return {
      background: `linear-gradient(to right, ${props.gradientFrom}, ${props.gradientTo})`,
    }
  }
  if (props.variant === 'royal-gradient') {
    return {
      background: 'linear-gradient(to right, #5763FF, #3342FF)',
    }
  }
  if (props.variant === 'royal-fade-gradient') {
    return {
      background: `linear-gradient(to right, #5763FF, ${props.trackColor})`,
    }
  }
  return {}
})
</script>

